import { Button } from "@/components/ui/button";
import { formatDuration, formatFileSize } from "@/features/utils/numbers";
import { useAudioPlayer } from "@/features/utils/use-audio";
import { PauseIcon, PlayIcon, TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { getFileIcon } from "./helpers";
import type { FilePreview } from "./types";
import { useTranslation } from "@/features/utils/i18n";

interface FileItemProps {
  file: File;
  fileKey: string;
  disabled: boolean;
  className?: string;
  onRemove: () => void;
}

export const FileItem = ({
  file,
  fileKey,
  disabled,
  className,
  onRemove,
}: FileItemProps) => {
  const { t } = useTranslation("fileUpload");
  const Icon = getFileIcon(file);
  const fileExt = file.name.split(".").pop()?.toUpperCase() || "";

  const audioId = useRef(`audio-${fileKey}-${Date.now()}`).current;
  const { isPlaying, play, pause, audioRef } = useAudioPlayer(audioId);
  const [previews, setPreviews] = useState<Map<string, FilePreview>>(new Map());
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  // Generate previews
  useEffect(() => {
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        setPreviews((prev) => {
          const updated = new Map(prev);
          updated.set(fileKey, {
            url,
            dimensions: `${img.width}×${img.height}`,
          });
          return updated;
        });
      };
      img.src = url;
    } else if (file.type.startsWith("audio/")) {
      const url = URL.createObjectURL(file);
      const audio = new Audio(url);
      audio.addEventListener("loadedmetadata", () => {
        setPreviews((prev) => {
          const updated = new Map(prev);
          updated.set(fileKey, { url, duration: audio.duration });
          return updated;
        });
      });
      audioRef.current = audio;
    }

    return () => {
      // Cleanup object URLs and audio elements
      previews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    };
  }, [fileKey]);

  const toggleAudio = useCallback(() => {
    if (isPlayingRef.current) {
      // Same audio playing - pause it
      pause();
    } else {
      play();
    }
  }, [isPlaying, play]);

  const preview = previews.get(fileKey);

  return (
    <div
      className={twMerge(
        "relative border border-input rounded-sm p-4 w-full overflow-hidden",
        className,
      )}
    >
      <div className="flex items-center gap-4 w-full">
        {/* Preview */}
        {file.type.startsWith("image/") && preview?.url ? (
          <img
            src={preview.url}
            alt={file.name}
            className="h-20 w-20 object-cover rounded flex-shrink-0"
          />
        ) : (
          <div className="h-20 w-20 flex items-center justify-center bg-slate-200 rounded flex-shrink-0">
            <Icon className="h-8 w-8 text-muted-foreground" weight="fill" />
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" title={file.name}>
            {file.name}
          </p>
          <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
            <span>{formatFileSize(file.size)}</span>
            {preview?.dimensions && <span>{preview.dimensions}</span>}
            {preview?.duration !== undefined && (
              <span>{formatDuration(preview.duration)}</span>
            )}
            <span>{fileExt}</span>
          </div>

          {/* Audio controls */}
          {file.type.startsWith("audio/") && preview?.url && toggleAudio && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => toggleAudio()}
            >
              {isPlaying ? (
                <PauseIcon className="h-3 w-3" weight="fill" />
              ) : (
                <PlayIcon className="h-3 w-3" weight="fill" />
              )}
              {isPlaying ? t("pause") : t("play")}
            </Button>
          )}
        </div>

        {/* Remove button */}
        <Button
          type="button"
          variant="destructive"
          size="icon"
          onClick={onRemove}
          disabled={disabled}
          className="flex-shrink-0"
        >
          <TrashIcon className="h-4 w-4" weight="fill" />
        </Button>
      </div>
    </div>
  );
};
